import React, { useCallback, useEffect, useState } from "react";
import { fetchEntitiesAll, performEntityAction } from "~/services";
import { COLORS } from "rylak-common/src/constants";
import { ENTITY_TYPE } from "~/constants";
import { TurnrCard, TurnrCardContent, TurnrCardHeader } from "~/components/card/TurnrCard.jsx";
import { formatReadableDateAndTimeShort, serviceHasError, serviceIsLoading, serviceIsSilentlyLoading, serviceIsSuccessfullyFetched, useServiceHook } from "rylak-common";
import TurnrInput from "~/components/input/TurnrInput.jsx";
import { getUserDataUserId } from "~/cores/appCore";
import { TurnrFontAwesomeIconButtonRound } from "~/components/buttons/icon-buttons/TurnrFontAwesomeIconButton";
import { faLocationDot, faLocationPin, faPaperPlaneTop } from "@fortawesome/pro-regular-svg-icons";
import SkeletonLoaderCardSlim from "~/components/loading/skeletons/SkeletonLoaderCardSlim.jsx";
import styles from "./OrderNotesCard.module.scss";
import classnames from "classnames";
import { TurnrTooltip } from "rylak-common/src/components/TurnrTooltip/TurnrTooltip";
import UserAvatar from "~/components/userAvatar/UserAvatar.jsx";
import TurnrEditIconButton from "~/components/buttons/icon-buttons/TurnrEditIconButton.jsx";
import LineDivider from "rylak-common/src/components/divider/LineDivider";

const createCommentTooltipTitle = "Add note";
const createCommentTooltipText = "Write a comment connected to this order. Anyone with access to this return order page will be able to read these notes.";
export default function OrderNotesCard(props) {
    let { appProps, orderId, title = "Notes", style } = props;

    const [editing, setEditing] = useState(false);
    const [messageInput, setMessageInput] = useState("");
    const [justCreatedComment, setJustCreatedComment] = useState(false);
    const [editCommentData, setEditCommentData] = useState(null);

    let { serviceData: messagesService, fetchServiceData: fetchMessagesService } = useServiceHook({
        appProps,
        fetchRequested: true,
        fetchData: {
            entityType: ENTITY_TYPE.ORDER_COMMENT,
            orderId: orderId,
            sort: ["isPinned,desc", "id,desc"],
        },
        onReceive: () => setJustCreatedComment(false),
        fetchFn: fetchEntitiesAll,
    });

    let { serviceData: createMessageService, fetchServiceData: createMessageServiceFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchData: {
            entityType: ENTITY_TYPE.ORDER,
            id: orderId,
        },
        fetchFn: performEntityAction,
    });

    let { serviceData: editMessageService, fetchServiceData: editMessageServiceFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchData: {
            entityType: ENTITY_TYPE.ORDER_COMMENT,
            orderId: orderId,
        },
        fetchFn: performEntityAction,
    });

    useEffect(() => {
        if (serviceIsSuccessfullyFetched(createMessageService)) {
            fetchMessagesService(null, { silentFetch: true });
        } else if (serviceHasError(createMessageService)) {
            setJustCreatedComment(false);
        }
    }, [createMessageService]);

    useEffect(() => {
        if (serviceIsSuccessfullyFetched(editMessageService)) {
            fetchMessagesService(null, { silentFetch: true });
        }
    }, [editMessageService]);

    const onCreateClick = useCallback(() => {
        createMessageServiceFn({
            entityType: ENTITY_TYPE.ORDER,
            id: orderId,
            action: {
                type: "ORDER_CREATE_COMMENT",
                data: {
                    userId: getUserDataUserId(appProps),
                    text: messageInput,
                },
            },
        });
        setMessageInput("");
        setJustCreatedComment(true);
    }, [messageInput]);

    const onPinClick = useCallback(({ id, isPinned }) => {
        editMessageServiceFn({
            entityType: ENTITY_TYPE.ORDER_COMMENT,
            id: id,
            action: {
                type: "ORDER_COMMENT_EDIT",
                data: {
                    isPinned: isPinned,
                },
            },
        });
    }, []);

    const onEditCommentClick = useCallback(({ message }) => {
        setEditCommentData({ text: message.text, message: message });
    }, []);

    const onEditCommentConfirm = useCallback(() => {
        editMessageServiceFn({
            entityType: ENTITY_TYPE.ORDER_COMMENT,
            id: editCommentData?.message?.id,
            action: {
                type: "ORDER_COMMENT_EDIT",
                data: {
                    text: editCommentData?.text,
                },
            },
        });
        setEditCommentData(null);
    }, [editCommentData]);

    const loadingMessages = serviceIsLoading(messagesService);
    const loadingCreate = serviceIsLoading(createMessageService);
    const loadingCreateOrSilentlyLoadingMessages = loadingCreate || serviceIsSilentlyLoading(messagesService);

    return (
        <TurnrCard loading={loadingMessages} style={{ ...style, borderRadius: 0 }}>
            <TurnrCardHeader
                title={
                    <div style={{ position: "relative" }}>
                        <div
                            style={{
                                display: "flex",
                                position: "absolute",
                                width: "100%",
                                height: "100%",
                                visibility: editing && "hidden",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            {title} <TurnrEditIconButton color={COLORS.BROWN} onClick={() => setEditing(true)} />
                        </div>

                        <div
                            style={{
                                visibility: !editing && "hidden",
                                maxWidth: "500px",
                                display: "flex",
                                alignItems: "center",
                                gridGap: "10px",
                                marginTop: "-5px",
                                marginBottom: "-5px",
                            }}
                        >
                            <TurnrInput
                                key={editing}
                                autoFocus={true}
                                disabled={loadingMessages || loadingCreate}
                                placeholder={createCommentTooltipTitle}
                                tooltip={createCommentTooltipText}
                                onEnter={onCreateClick}
                                value={messageInput}
                                onChange={setMessageInput}
                                debounce={false}
                                fullWidth={true}
                            />
                            <TurnrFontAwesomeIconButtonRound
                                onClick={onCreateClick}
                                disabled={loadingMessages || loadingCreate || !messageInput}
                                color={COLORS.ACTION}
                                icon={faPaperPlaneTop}
                                size={"medium"}
                            />
                        </div>
                    </div>
                }
            />
            {messagesService?.data?.length > 0 && <LineDivider />}
            {(messagesService?.data?.length > 0 || loadingCreateOrSilentlyLoadingMessages) && (
                <TurnrCardContent>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gridGap: "15px",
                        }}
                    >
                        {" "}
                        {loadingCreateOrSilentlyLoadingMessages && justCreatedComment && (
                            <div
                                style={{
                                    width: "100%",
                                    height: "35px",
                                    position: "relative",
                                }}
                            >
                                <SkeletonLoaderCardSlim></SkeletonLoaderCardSlim>
                            </div>
                        )}
                        {messagesService?.data?.map((messageData) => {
                            return (
                                <div
                                    key={messageData?.id}
                                    className={styles["message"]}
                                    style={{
                                        display: "flex",
                                        // alignItems: "center",
                                        gridGap: "10px",
                                        alignItems: "space-between",
                                    }}
                                >
                                    <UserAvatar user={messageData?.user} />
                                    <div
                                        style={{
                                            marginTop: "3px",
                                            width: "100%",
                                        }}
                                        onClick={() => {
                                            if (messageData?.user?.id === getUserDataUserId(appProps)) {
                                                onEditCommentClick({
                                                    message: messageData,
                                                });
                                            }
                                        }}
                                    >
                                        {editCommentData?.message?.id === messageData?.id ? (
                                            <TurnrInput
                                                onEnter={() => onEditCommentConfirm()}
                                                onLostFocus={() => setEditCommentData(null)}
                                                autoFocus={true}
                                                fullWidth={true}
                                                value={editCommentData?.text}
                                                onChange={(v) =>
                                                    setEditCommentData({
                                                        text: v,
                                                        message: messageData,
                                                    })
                                                }
                                            ></TurnrInput>
                                        ) : (
                                            <div style={{ fontSize: "13px" }}>{messageData?.text}</div>
                                        )}
                                        <div
                                            style={{
                                                marginTop: "4px",
                                                fontSize: "10px",
                                                color: COLORS.WEAK_TEXT,
                                            }}
                                        >
                                            {formatReadableDateAndTimeShort({ dateTime: messageData?.createdAt })}
                                        </div>
                                    </div>
                                    <div className={classnames(styles["pin-button"], messageData?.pinned && styles["pin-button-pinned"])} style={{ marginLeft: "auto" }}>
                                        <TurnrTooltip content={messageData?.pinned ? "Un-pin this comment" : "Pin this comment"}>
                                            <TurnrFontAwesomeIconButtonRound
                                                onClick={() =>
                                                    onPinClick({
                                                        id: messageData?.id,
                                                        isPinned: !messageData?.pinned,
                                                    })
                                                }
                                                tooltip="Pin this comment"
                                                // size={"small"}
                                                icon={messageData?.pinned ? faLocationDot : faLocationPin}
                                            />
                                        </TurnrTooltip>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </TurnrCardContent>
            )}
        </TurnrCard>
    );
}
